// A field set, in field order, with the one thing a form gets wrong.
//
// The thing forms get wrong is what happens after somebody presses the button
// and nothing appears to happen. The message is usually somewhere below the
// fold, in red, in eleven pixels, and the person standing at the stand
// concludes the tablet is broken. So: a summary at the top, focus moved to it,
// role="alert" so it is spoken, and every line in it a control that jumps to
// the field it names.
//
// Order is the only layout a client controls, per section 4 of the field
// library. There are no columns and no sections here to configure.
//
// WHEN A REFUSAL IS ALLOWED ON SCREEN, WHICH IS THE OTHER THING FORMS GET WRONG.
//
// A verdict exists for every field from the moment the form opens, because
// validateField answers `missing` for an empty required field and empty is what
// a fresh form is full of. Handing those straight to Field opens a registration
// with a refusal under every box before the attendee has touched anything, and
// puts one under the address they are half way through typing. Both are the
// tablet telling a stranger they are wrong before they have finished the word,
// which is the same mistake that made the email message a block in the first
// place: a message that fires on something that is about to be fine is a
// message people learn to tap past, including the ones that matter.
//
// So this component decides when, and the rule has two regimes.
//
//   Before anybody has tried to finish, a field's verdict appears when they
//   leave it and goes away again on the next keystroke. The form opens clean,
//   nothing appears under a finger, and a message that is on screen is always
//   about something the person has stopped working on.
//
//   Once somebody has pressed Finish, everything shows and stays showing, live.
//   That does reintroduce a message that can change mid-word, and it is the
//   right trade from that point on: they have asked the question, so the answer
//   is no longer unsolicited, and they are working down a list and need items to
//   come off it as they are fixed rather than only when they move away. It also
//   keeps each field in step with the summary above, which is computed live and
//   would otherwise be contradicted by the field it points at.
//
// This is display only. `problems` is still the caller's, validateFieldSet still
// answers over every field, and Finish is still blocked by a verdict nobody has
// been shown yet. Hiding a refusal is not the same as not having one.
//
// It lives here rather than in each surface for two reasons. The capture surface
// and the harness station both render this, and the harness had already grown
// its own `touched` map by hand, which is two copies of one rule and the first
// place they disagree is the day one of them is changed. And the QR self-serve
// surface, which is not built yet, gets this by rendering a FieldSet rather than
// by somebody remembering. The cost is state in a component that had almost
// none; it is display timing rather than anything an attendee typed, so it is
// not a second copy of an answer for PrivateSession's clear to miss.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Field, fieldControlId } from "./Field.js";
import type { FieldDefinition, FieldValue, FieldValues } from "./types.js";
import { fieldLabel } from "./types.js";
import { isVisible, type Audience, type Verdict } from "./validate.js";

export type FieldSetProps = {
  fields: readonly FieldDefinition[];
  values: FieldValues;
  onChange: (field_id: string, value: FieldValue) => void;
  /** Attendee by default. Crew fields never render to an attendee. Section 3.10. */
  audience?: Audience | undefined;
  /**
   * Every field's verdict, keyed by `field_id`. Absent means nothing has been
   * checked yet.
   *
   * Pass all of them. This component decides which are allowed on screen and
   * when; a caller that filters first is a second copy of that rule.
   */
  problems?: Readonly<Record<string, Verdict>> | undefined;
  /**
   * Set when somebody has tried to finish.
   *
   * Two things follow from it and the second is easy to miss: the summary
   * appears, and every field's verdict is released from the leave-the-field
   * rule above and shown live from then on. Named for the first because that is
   * what it did when it was added and renaming it would rename a prop two
   * surfaces pass.
   */
  showSummary?: boolean | undefined;
  /** Registration handoff: release inline errors but do not draw the summary card. */
  summaryPresentation?: "list" | "fields" | undefined;
  disabled?: boolean | undefined;
  onBlur?: ((field_id: string) => void) | undefined;
  /** Registration presentation: keep the semantic label, draw its copy as the placeholder. */
  labelsAsPlaceholders?: boolean | undefined;
};

/** Move focus to a field's control. Used by the summary and after a failed finish. */
export function focusField(field_id: string): void {
  const control = document.getElementById(fieldControlId(field_id));
  if (control instanceof HTMLElement) {
    control.focus();
    control.scrollIntoView({ block: "center" });
  }
}

export function FieldSet({
  fields,
  values,
  onChange,
  audience = "attendee",
  problems,
  showSummary,
  summaryPresentation = "list",
  disabled,
  onBlur,
  labelsAsPlaceholders,
}: FieldSetProps): ReactNode {
  const visible = fields.filter((field) => isVisible(field, values, audience));

  const blocking: { field: FieldDefinition; message: string }[] = [];
  for (const field of visible) {
    const problem = problems?.[field.field_id];
    if (problem?.state === "block") blocking.push({ field, message: problem.message });
  }

  const summary = useRef<HTMLDivElement>(null);
  const attempted = showSummary === true && blocking.length > 0;
  const shown = attempted && summaryPresentation === "list";

  // Focus moves once per attempt to finish, not on every keystroke that
  // changes the list. Somebody correcting the first problem should not be
  // thrown back to the top of the page while they are typing.
  const attempt = useRef(0);
  useEffect(() => {
    if (!attempted) {
      attempt.current = 0;
      return;
    }
    if (attempt.current === 0) {
      attempt.current = 1;
      if (shown) summary.current?.focus();
      else if (blocking[0] !== undefined) focusField(blocking[0].field.field_id);
    }
  }, [attempted, shown, blocking]);

  // The fields that have been left and not returned to. See the header.
  const [rested, setRested] = useState<ReadonlySet<string>>(() => new Set());

  // The same set both ways round, and each returns the set it was given when
  // nothing moved, so typing a tenth character into a field nobody has left
  // does not re-render the whole set.
  const changed = useCallback(
    (field_id: string, value: FieldValue) => {
      setRested((current) => {
        if (!current.has(field_id)) return current;
        const next = new Set(current);
        next.delete(field_id);
        return next;
      });
      onChange(field_id, value);
    },
    [onChange],
  );

  const left = useCallback(
    (field_id: string) => {
      setRested((current) => (current.has(field_id) ? current : new Set(current).add(field_id)));
      onBlur?.(field_id);
    },
    [onBlur],
  );

  return (
    <div className="xp-stack">
      {shown ? (
        <div className="xp-summary" role="alert" tabIndex={-1} ref={summary}>
          <h2 className="xp-summary__h">Check these before you carry on.</h2>
          <ul className="xp-summary__list">
            {blocking.map(({ field, message }) => (
              <li key={field.field_id}>
                <button
                  type="button"
                  className="xp-summary__go"
                  onClick={() => focusField(field.field_id)}
                >
                  {fieldLabel(field)}. {message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {visible.map((field) => (
        <Field
          key={field.field_id}
          field={field}
          value={values[field.field_id] ?? null}
          onChange={changed}
          problem={
            showSummary === true || rested.has(field.field_id)
              ? problems?.[field.field_id]
              : undefined
          }
          disabled={disabled}
          onBlur={left}
          labelsAsPlaceholders={labelsAsPlaceholders}
        />
      ))}
    </div>
  );
}
